import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import LoadingModal from "../../components/LoadingModal";
import AccountAppointment from "../../models/AccountAppointment";
import AppointmentApplicationType from "../../models/AppointmentApplicationType";
import accountService from "../../services/AccountService";
import utilsService from "../../services/UtilsService";
import { StyledTableCell, StyledTableRow } from "../../theme/GlobalTheme";
import OnsiteAppointmentModal from "./OnsiteAppointmentModal";

function OnsiteAppointmentScreen() {
  const [modalOpen, setModalOpen] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState<
    AppointmentApplicationType[]
  >([]);
  const [appointments, setAppointments] = useState<AccountAppointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDesc, setDialogDesc] = useState("");
  const [dialogButton, setDialogButton] = useState<
    JSX.Element | JSX.Element[] | undefined
  >();

  const loadAppointments = useCallback(
    async (appts: AccountAppointment[] = []) => {
      if (appts.length > 0) {
        setAppointments(appts);
        return;
      }
      const data = await accountService.getAppointments();
      setAppointments(data);
    },
    [],
  );

  function deleteAppointment(appointmentId: number) {
    setDialogTitle("Confirmar");
    setDialogDesc("¿Estas seguro que deseas borrar este cita?");
    setOpenDialog(true);
    setDialogButton([
      <Button
        color="error"
        variant="contained"
        onClick={async () => {
          setOpenDialog(false);
          try {
            setIsSaving(true);
            const updatedAppointments =
              await accountService.deleteAppointment(appointmentId);
            setAppointments(updatedAppointments);
            setIsSaving(false);
          } catch {
            setIsSaving(false);
            setDialogTitle("Error");
            setDialogDesc("Ha ocurrido un error. Por favor intente mas tarde");
            setOpenDialog(true);
            setDialogButton(
              <Button onClick={() => setOpenDialog(false)}>Ok</Button>,
            );
          }
        }}
      >
        Eliminar
      </Button>,
      <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>,
    ]);
  }

  useEffect(() => {
    async function loadData() {
      try {
        const [types] = await Promise.all([
          utilsService.getAppointmentApplicationTypes(),
          loadAppointments(),
        ]);
        setAppointmentTypes(types);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [loadAppointments]);

  return (
    <Box>
      <Box textAlign="center">
        <Typography
          display="inline-block"
          sx={{
            mb: 3,
            backgroundColor: "#00166B",
            color: "white",
            padding: "10px 30px",
            borderRadius: "10px",
            textTransform: "uppercase",
          }}
          variant="h4"
        >
          Lounge de Estudio
        </Typography>
      </Box>
      <Box>
        {isLoading ? (
          <Box textAlign="center">
            <CircularProgress color="secondary" size={50} sx={{ mr: "10px" }} />
            <Typography variant="body1">Cargando...</Typography>
          </Box>
        ) : (
          <>
            <Button
              sx={{ mb: 2 }}
              onClick={() => setModalOpen(true)}
              variant="contained"
            >
              Agendar Cita
            </Button>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">Fecha</StyledTableCell>
                    <StyledTableCell align="left">Tipo de Cita</StyledTableCell>
                    <StyledTableCell align="left">Estatus</StyledTableCell>
                    <StyledTableCell align="left"></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.length === 0 && (
                    <StyledTableRow>
                      <StyledTableCell sx={{ textAlign: "center" }} colSpan={4}>
                        No citas disponibles
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                  {appointments.map((appointment) => (
                    <StyledTableRow
                      key={appointment.solicitudCitaID}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <StyledTableCell align="left">
                        {appointment.fechaCita}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {appointment.solicitudCitaTipoDescripcion}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {appointment.solicitudCitaEstatus}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {appointment.solicitudCitaEstatus === "Pendiente" ? (
                          <IconButton
                            color="error"
                            onClick={() =>
                              deleteAppointment(appointment.solicitudCitaID)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : null}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <Dialog fullWidth maxWidth="sm" open={modalOpen}>
        <DialogTitle>Agendar Cita</DialogTitle>
        <DialogContent>
          <OnsiteAppointmentModal
            appointmentTypes={appointmentTypes}
            onClose={() => setModalOpen(false)}
            onSuccess={loadAppointments}
          />
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        maxWidth="sm"
        open={openDialog}
        title={dialogTitle}
        description={dialogDesc}
        buttons={dialogButton}
      />
      <LoadingModal isLoading={isSaving} loadingMessage="Guardando" />
    </Box>
  );
}

export default OnsiteAppointmentScreen;
